import * as THREE from 'three';

// Draws a streak up to 9x (shifted by ±size on each axis) so any part crossing
// a canvas edge reappears correctly on the opposite edge — without this, tiled
// (RepeatWrapping) textures show a hard visible seam at every repeat boundary,
// since a streak that gets cut off at x=size just stops instead of continuing
// from x=0.
const drawWrappedStreak = (ctx, size, x, y, len, dy2, strokeStyle, lineWidth) => {
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    for (const ox of [0, -size, size]) {
        for (const oy of [0, -size, size]) {
            ctx.beginPath();
            ctx.moveTo(x + ox, y + oy);
            ctx.lineTo(x + len + ox, y + dy2 + oy);
            ctx.stroke();
        }
    }
};

let _cachedGrainTexture = null;

// Prieš pasirenkant realią dekoro plokštę, baldo paviršiai buvo visiškai lygi
// vienspalvė medžiaga — jokio roughness/bump varijavimo, todėl net gera šviesa
// neturėjo ką "pagriebti" ir paviršius atrodė kaip plastikas, o ne medžio plokštė.
// Šis modulis vieną kartą sugeneruoja nedidelę procedūrinę (ne tikro medžio nuotrauka)
// grūdėtumo/šiurkštumo teksūrą canvas'e ir naudoja ją kaip bumpMap — pakanka, kad
// atsirastų subtilūs mikro-šešėliai, bet neapsimeta realiu dekoru.
export const getGrainBumpTexture = () => {
    if (_cachedGrainTexture) return _cachedGrainTexture;

    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    // Neutrali pilka bazė — bumpMap naudoja šviesumą kaip santykinį aukštį
    // (128 = be poslinkio, šviesiau = pakelta, tamsiau = įdubusi).
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size, size);

    // Ilgi, ploni VERTIKALŪS brūkšniai — apytikris medžio skaidulų imitavimas (sutampa
    // kryptimi su getDefaultWoodColorTexture spalvotu raštu), tik tiek krypties
    // variacijos, kad šviesa turėtų ką pagriebti. Brėžiama su wrap'u (drawWrappedStreak),
    // kad tekstūra kartotųsi be matomos siūlės.
    for (let i = 0; i < 220; i++) {
        const x = Math.random() * size;
        const len = 40 + Math.random() * (size - 40);
        const y = Math.random() * size;
        const shade = 118 + Math.random() * 20;
        drawWrappedStreak(ctx, size, x, y, (Math.random() - 0.5) * 3, len,
            `rgba(${shade},${shade},${shade},${0.15 + Math.random() * 0.2})`, 0.6 + Math.random() * 1.1);
    }

    // Smulkus triukšmas virš brūkšnių, kad paviršius nebūtų idealiai lygus net tarp jų.
    const imgData = ctx.getImageData(0, 0, size, size);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
        const n = (Math.random() - 0.5) * 14;
        d[i] = d[i + 1] = d[i + 2] = Math.min(255, Math.max(0, d[i] + n));
    }
    ctx.putImageData(imgData, 0, 0);

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    // Fiksuotas kartojimas (ne pagal konkretaus skydo matmenis) — šis teksūros
    // objektas yra BENDRAS/talpykloje visiems skydams/durelėms, o three.js .repeat
    // yra paties teksūros objekto, ne mesh'o, savybė. Jei kiekvienas naudotojas jį
    // perrašinėtų pagal savo plotį, visi kiti naudotojai (ta pati teksūra) irgi
    // pasikeistų. Kadangi tai tik subtilus grūdėtumas, o ne tikslus dekoro raštas,
    // fiksuota reikšmė visiškai tinka.
    tex.repeat.set(6, 6);
    tex.needsUpdate = true;
    _cachedGrainTexture = tex;
    return tex;
};

let _cachedWoodColorTexture = null;

// Šviesi (balta/off-white) SPALVOTA (ne vien bumpMap) tekstūra numatytajam, dar
// nepasirinktos dekoro plokštės vaizdui — vien vientisa spalva atrodė plokščia/
// plastikinė, o vien bumpMap be spalvų varijavimo pačios spalvos neišsprendžia.
// Tai vis tiek procedūrinė (canvas), ne tikra nuotrauka — tik tiek subtilaus
// rašto, kad numatytasis vaizdas neatrodytų kaip dažytas plastikas.
export const getDefaultWoodColorTexture = () => {
    if (_cachedWoodColorTexture) return _cachedWoodColorTexture;

    const size = 256;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#e6e0d2';
    ctx.fillRect(0, 0, size, size);

    // Švelnios, plačios tonų juostos — dabar VERTIKALIOS (kinta pagal x, ne y), kad
    // atitiktų vertikalią rašto kryptį (žr. brūkšnelius žemiau). Sinusai su DVIEM
    // pilnais periodais per canvas'o plotį (2π*2*x/size) matematiškai garantuoja,
    // kad x=0 ir x=size reikšmės sutampa, tad kartojant tekstūrą (RepeatWrapping)
    // NĖRA matomos siūlės ties kiekvieno pakartojimo riba.
    const bandImg = ctx.getImageData(0, 0, size, size);
    const bd = bandImg.data;
    for (let x = 0; x < size; x++) {
        const tone = Math.sin((x / size) * Math.PI * 2 * 2.5) * 7 + Math.sin((x / size) * Math.PI * 2 * 5.5 + 1.3) * 4;
        for (let y = 0; y < size; y++) {
            const idx = (y * size + x) * 4;
            bd[idx] = Math.min(255, Math.max(0, bd[idx] + tone));
            bd[idx + 1] = Math.min(255, Math.max(0, bd[idx + 1] + tone));
            bd[idx + 2] = Math.min(255, Math.max(0, bd[idx + 2] + tone * 0.85));
        }
    }
    ctx.putImageData(bandImg, 0, 0);

    // Ilgi, ploni brūkšniai — dabar VERTIKALŪS (ilgis Y kryptimi, nedidelis
    // dyzelis X kryptimi), imituoja realų baldo plokščių dekoro rašto kryptį
    // (vertikalus, ne horizontalus). Šviesesni beveik pasiekia gryną baltą,
    // tamsesni — švelnų šiltą pilką, kad neatrodytų kaip pilkas dažas.
    for (let i = 0; i < 260; i++) {
        const x = Math.random() * size;
        const len = 40 + Math.random() * (size - 40);
        const y = Math.random() * size;
        const darker = Math.random() > 0.55;
        const base = darker ? [208, 200, 184] : [250, 248, 242];
        drawWrappedStreak(ctx, size, x, y, (Math.random() - 0.5) * 4, len,
            `rgba(${base[0]},${base[1]},${base[2]},${0.22 + Math.random() * 0.22})`, 0.6 + Math.random() * 1.3);
    }

    const imgData = ctx.getImageData(0, 0, size, size);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
        const n = (Math.random() - 0.5) * 6;
        d[i] = Math.min(255, Math.max(0, d[i] + n));
        d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + n));
        d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + n));
    }
    ctx.putImageData(imgData, 0, 0);

    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(4, 4);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    _cachedWoodColorTexture = tex;
    return tex;
};
